import React from 'react';
import {
  BooleanInput,
  maxLength,
  maxValue,
  minLength,
  minValue,
  number,
  NumberInput,
  required,
  SelectInput,
  TextInput,
  ReferenceArrayInput,
  useTranslate,
  ReferenceInput,
} from 'react-admin';
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Tooltip,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import {
  formatScheduleTypeName,
  getServiceEntity,
  perPage,
} from '../../../helper';
import EditablePriceGrid from './EditablePriceGrid';
import ReferenceArrayChild from '../../../components/ReferenceArrayChild';

const useStyles = makeStyles(() => ({
  paperBase: {
    padding: '40px 30px',
    margin: '15px 0',
  },
  w100: {
    width: '100%',
  },
  formRoot: {
    '& .MuiTextField-root': {
      width: '100%',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: '#fdfdfd',
    },
  },
  pLR10: {
    padding: '0 10px',
  },
  center: {
    textAlign: 'center',
  },
  clickable: {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  inline: {
    '&.MuiFormGroup-root': {
      display: 'inline',
    },
    '& .MuiFormHelperText-root': {
      display: 'none',
    },
  },
}));

const twoColumnProps = {
  item: true,
  xs: 12,
  sm: 6,
};

const validateShortName = [required(), minLength(2), maxLength(255)];
const validateLaborHours = [required(), number(), minValue(0), maxValue(500)];
const validateScheduleTypes = required();

const BasicsForm = (props) => {
  const {
    record,
    priceSheets,
    scheduleTypes,
    setSelectedScheduleTypeIds,
    singlePartsCost,
    setSinglePartsCost,
    singleLaborCost,
    setSingleLaborCost,
    displayRef,
    isBookable,
    isShowingAdvanced,
    setIsShowingAdvanced,
    allowMultipleScheduleTypes,
    errorMessage,
  } = props;
  const classes = useStyles();
  const translate = useTranslate();

  const scrollToDisplay = () => {
    if (!isShowingAdvanced) {
      setIsShowingAdvanced(true);
    }
    setTimeout(() => {
      displayRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }, 500);
  };

  const singlePartsCostIsEmpty =
    errorMessage && !singlePartsCost && singlePartsCost !== 0;
  const singleLaborCostIsEmpty =
    errorMessage && !singleLaborCost && singleLaborCost !== 0;

  return (
    <Paper className={classes.paperBase}>
      <Typography variant="h5" gutterBottom>
        Basics
      </Typography>
      <Grid container className={classes.formRoot}>
        <Grid className={classes.pLR10} {...twoColumnProps}>
          <div>
            <h30>Service Name: </h30  >
            <label>{record.short_name}</label>
          </div>
        </Grid>
        <Tooltip
          title="This is how long it takes for a technician to perform this service"
          placement="right"
        >
          <Grid {...twoColumnProps} className={classes.pLR10}>
            <NumberInput
              resource="service-offering"
              validate={validateLaborHours}
              source="labor_hours"
            />
          </Grid>
        </Tooltip>

        <Grid container direction="row" xs={12}>
          {record.short_name != 'New Tire Installation' && priceSheets.length === 1 && (
            <>
              <Grid {...twoColumnProps} className={classes.pLR10}>
                <TextField
                  variant="filled"
                  margin="dense"
                  required
                  error={singlePartsCostIsEmpty}
                  helperText={singlePartsCostIsEmpty ? 'Required' : ' '}
                  label={translate('resources.price.fields.parts_cost_dollars')}
                  value={singlePartsCost}
                  onChange={(e) => setSinglePartsCost(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid {...twoColumnProps} className={classes.pLR10}>
                <TextField
                  variant="filled"
                  margin="dense"
                  required
                  error={singleLaborCostIsEmpty}
                  helperText={singleLaborCostIsEmpty ? 'Required' : ' '}
                  label={translate('resources.price.fields.labor_cost_dollars')}
                  value={singleLaborCost}
                  onChange={(e) => setSingleLaborCost(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          )}

          {scheduleTypes.length > 1 && (
            <Tooltip
              title={`How individual services are grouped and connected to your ${getServiceEntity(
                true,
                'lower',
              )} and the booking site`}
              placement="right"
            >
              <Grid {...twoColumnProps} className={classes.pLR10}>
                {/* If a service offering already has multiple schedule types OR url param shouldAllowMultipleScheduleTypes=true, show array input. Else, only allow single input */}
                {allowMultipleScheduleTypes ||
                record.schedule_type_ids?.length > 1 ? (
                  <ReferenceArrayInput
                    source="schedule_type_ids"
                    reference="schedule-type"
                    perPage={perPage}
                    sort={{ field: 'name', order: 'ASC' }}
                    validate={[required()]}
                    className={classes.w100}
                  >
                    <ReferenceArrayChild
                      {...props}
                      optionText={(option) =>
                        formatScheduleTypeName(option.name, translate)
                      }
                      source="schedule_type_ids"
                      onChange={(e) => {
                        setSelectedScheduleTypeIds(e.target.value);
                      }}
                    />
                  </ReferenceArrayInput>
                ) : (
                  <ReferenceInput
                    source="schedule_type_ids"
                    reference="schedule-type"
                    perPage={perPage}
                    sort={{ field: 'name', order: 'ASC' }}
                    validate={[required()]}
                    className={classes.w100}
                    format={(arr) => arr?.[0]}
                    parse={(el) => [el]}
                    onChange={(e) =>
                      setSelectedScheduleTypeIds([e.target.value])
                    }
                  >
                    <SelectInput
                      resource="service-offering"
                      source="schedule_type_ids"
                      label="Schedule Type"
                      validate={validateScheduleTypes}
                      optionText={(option) =>
                        formatScheduleTypeName(option.name, translate)
                      }
                    />
                  </ReferenceInput>
                )}
              </Grid>
            </Tooltip>
          )}
        </Grid>

        <Tooltip
          title="Customers can select this service on the booking site"
          placement="left"
        >
          <Grid item xs={12} className={classes.pLR10}>
            <BooleanInput
              resource="service-offering"
              source="bookable"
              initialValue={false}
              className={classes.inline}
            />
            {isBookable && (
              <Typography
                component="span"
                className={classes.clickable}
                onClick={scrollToDisplay}
              >
                View how it&rsquo;s displayed
              </Typography>
            )}
          </Grid>
        </Tooltip>
        <Tooltip
          title="Technicians can recommend this service as an upsell"
          placement="left"
        >
          <Grid item xs={12} className={classes.pLR10}>
            <BooleanInput
              resource="service-offering"
              source="upsellable"
              initialValue={false}
              className={classes.inline}
            />
          </Grid>
        </Tooltip>
        <Tooltip
          title="Shows “Starting at” before pricing on the booking site"
          placement="left"
        >
          <Grid item xs={12} className={classes.pLR10}>
            <BooleanInput
              resource="service-offering"
              source="variable_pricing"
              className={classes.inline}
            />
          </Grid>
        </Tooltip>
      </Grid>

      {priceSheets.length > 1 && (
        <>
          <Typography variant="h5" gutterBottom>
            Pricing
          </Typography>
          <Grid container className={classes.formRoot}>
            <Grid item xs={12}>
              <EditablePriceGrid {...props} />
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );
};

export default BasicsForm;
